import React, { useMemo, useState } from "react";
import api from "../services/api";
import { activeTab, useConsole } from "../lib/console";
import { fmtDateTime, num, pct } from "../lib/format";
import { Badge, Button, Callout, Card, Empty, ErrorState, KV, Meter, SkeletonRows, Spinner, Stat } from "../components/ui";
import { Icon } from "../components/Icon";
import { BarList } from "../components/charts";
import { PageHeader, PageTabs } from "../components/ConsoleShell";
import { useDLMetrics } from "./Response";

const FEATURE_LABELS = {
  duration_ms: "Connection duration",
  bytes_sent: "Bytes sent",
  bytes_recv: "Bytes received",
  packet_count: "Packet count",
  byte_ratio: "Sent / received ratio",
  avg_packet_size: "Average packet size",
  dst_port_norm: "Destination port",
  protocol_id: "Protocol",
  is_well_known_port: "Well-known port",
  is_ephemeral_port: "Ephemeral port",
  hour_sin: "Time of day (sine)",
  hour_cos: "Time of day (cosine)",
  src_port_norm: "Source port",
  connection_rate: "Connection rate",
  payload_entropy: "Payload entropy",
};
const featureLabel = (f) => FEATURE_LABELS[f] || String(f).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const PIPELINE = [
  { icon: "sparkle", name: "Isolation Forest", role: "Unsupervised anomaly detection", body: "Flags events that are easy to isolate from normal traffic." },
  { icon: "layers", name: "Random Forest", role: "Attack classification", body: "Assigns one of the attack classes and its confidence." },
  { icon: "trendUp", name: "Gradient Boosting", role: "Threat probability", body: "Binary threat / benign probability used for severity." },
  { icon: "activity", name: "Autoencoder", role: "Reconstruction anomaly", body: "Events it cannot reconstruct well are treated as unusual." },
];

function DetectionModels() {
  const { mlMetrics: m } = useConsole();
  const features = useMemo(
    () => (m?.feature_importance || []).map((f) => ({ key: f.feature, label: featureLabel(f.feature), value: Math.round(f.importance * 1000) / 10, color: "var(--brand)" })),
    [m],
  );
  if (!m) return <Card><SkeletonRows rows={8} /></Card>;
  const trained = m.accuracy != null;

  return (
    <>
      <Callout tone="medium" icon="info" title="How to read these numbers">
        The models are trained at start-up on {m.samples ? num(m.samples) : "synthetic"} synthetic samples shaped on CICIDS2017 / UNSW-NB15 traffic distributions
        {m.smote_used ? ", balanced with SMOTE" : ""}. Accuracy, precision, recall, F1 and AUC are measured in-sample on that data; the {m.cv_folds || 5}-fold cross-validated F1 is the
        fairer estimate. None of these figures is a claim about accuracy on real-world traffic.
      </Callout>
      <div className="grid cols-4" style={{ marginTop: 16 }}>
        <Stat label="Cross-validated F1" icon="checkCircle" tone="brand" value={m.cv_f1_mean != null ? pct(m.cv_f1_mean) : "—"} sub={m.cv_f1_std != null ? `± ${pct(m.cv_f1_std)} across ${m.cv_folds || 5} folds` : "Not available"} />
        <Stat label="Predictions made" icon="activity" tone="signal" value={num(m.total_predictions)} sub="Since the backend started" />
        <Stat label="Flagged as threats" icon="target" tone="high" value={num(m.threat_count)} sub={m.detection_rate != null ? `${pct(m.detection_rate)} of predictions` : " "} />
        <Stat label="Analyst feedback queued" icon="message" tone="ok" value={num(m.feedback_buffer || 0)} sub="Used in the next online update" />
      </div>
      <div className="grid cols-12" style={{ marginTop: 16 }}>
        <Card className="span-5" title="In-sample scores" subtitle="Random Forest on the training set" icon="gauge">
          {!trained ? (
            <Empty compact icon="clock">
              Models are still training.
            </Empty>
          ) : (
            <div className="col" style={{ gap: 14 }}>
              {[
                ["Accuracy", m.accuracy],
                ["Precision (weighted)", m.precision],
                ["Recall (weighted)", m.recall],
                ["F1 score (weighted)", m.f1_score],
                ["ROC AUC (multi-class)", m.auc],
                ["ROC AUC (threat vs benign)", m.auc_binary],
              ]
                .filter(([, v]) => v != null)
                .map(([l, v]) => (
                  <div key={l}>
                    <div className="row-between small" style={{ marginBottom: 6 }}>
                      <span className="dim">{l}</span>
                      <span className="tnum strong">{pct(v)}</span>
                    </div>
                    <Meter value={v * 100} label={l} />
                  </div>
                ))}
            </div>
          )}
        </Card>
        <Card className="span-7" title="What the classifier relies on" subtitle="Random Forest feature importance" icon="sliders">
          <BarList items={features} max={10} share={false} valueLabel={(v) => `${v}%`} empty="Feature importance appears once the model is trained." />
        </Card>
      </div>
      <div className="grid cols-12" style={{ marginTop: 16 }}>
        <Card className="span-8" title="Detection pipeline" subtitle="Every event passes through all four models" icon="layers">
          <div className="grid cols-2" style={{ gap: 12 }}>
            {PIPELINE.map((p, i) => (
              <div key={p.name} className="card-inset" style={{ display: "flex", gap: 12 }}>
                <span className="stat-icon tone-brand" style={{ flexShrink: 0 }}>
                  <Icon name={p.icon} size={15} />
                </span>
                <div>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="mono tiny muted">{String(i + 1).padStart(2, "0")}</span>
                    <strong style={{ fontSize: 13.5 }}>{p.name}</strong>
                  </div>
                  <div className="small" style={{ color: "var(--brand)", marginTop: 2 }}>
                    {p.role}
                  </div>
                  <div className="small muted" style={{ marginTop: 4 }}>
                    {p.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="span-4" title="Training run" icon="database">
          <KV
            cols={1}
            items={[
              ["Trained at", m.trained_at ? `${fmtDateTime(m.trained_at)} GST` : "—"],
              ["Training samples", m.samples ? `${num(m.samples)} (${num(m.balanced_samples)} after balancing)` : "—"],
              ["Classes", m.classes ? `${m.classes.length} attack classes` : "—"],
              ["Explanations (SHAP)", m.shap_available ? "Available" : "Not installed"],
            ]}
          />
        </Card>
      </div>
    </>
  );
}

function ModelCard({ icon, title, purpose, trained, metrics }) {
  return (
    <Card
      title={title}
      subtitle={purpose}
      icon={icon}
      actions={
        trained ? (
          <Badge tone="ok" dot>
            Trained
          </Badge>
        ) : (
          <Badge tone="neutral">Not ready</Badge>
        )
      }
    >
      <KV items={metrics.map(([k, v, hint]) => [k, <span title={hint}>{v}</span>])} />
    </Card>
  );
}

function CnnTry({ ready }) {
  const [text, setText] = useState("");
  const [state, setState] = useState({ loading: false, result: null, error: null });
  const run = async (e) => {
    e.preventDefault();
    setState({ loading: true, result: null, error: null });
    try {
      const r = await api.post("/api/dl/cnn-scan", { text });
      setState({ loading: false, result: r.data, error: null });
    } catch (err) {
      setState({ loading: false, result: null, error: err?.response?.data?.detail || err.message });
    }
  };
  const r = state.result;
  return (
    <Card title="Try the malware CNN" subtitle="Scores the first 256 bytes of any text you paste" icon="bug">
      <form onSubmit={run} className="col" style={{ gap: 10 }}>
        <textarea
          className="textarea input-mono"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste a script, command line or payload…"
          aria-label="Payload to scan"
          spellCheck={false}
        />
        <div className="row-between">
          <span className="tiny muted">Runs on the server's trained model. Nothing is stored.</span>
          <Button type="submit" variant="primary" icon="search" loading={state.loading} disabled={!ready || !text.trim()}>
            Scan
          </Button>
        </div>
      </form>
      {state.error && (
        <Callout tone="critical" icon="alertCircle" className="drawer-section">
          {state.error}
        </Callout>
      )}
      {r && (
        <div className="card-inset row-between" style={{ marginTop: 14 }}>
          <div>
            <div className="eyebrow">Verdict</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: r.verdict === "MALICIOUS" ? "var(--sev-critical)" : "var(--ok)" }}>
              {r.verdict === "MALICIOUS" ? "Malicious" : r.verdict === "BENIGN" ? "Benign" : "Unknown"}
            </div>
          </div>
          <div style={{ minWidth: 160 }}>
            <div className="row-between small" style={{ marginBottom: 6 }}>
              <span className="dim">Malicious probability</span>
              <span className="tnum strong">{pct(r.malicious_prob)}</span>
            </div>
            <Meter value={(r.malicious_prob || 0) * 100} tone={r.malicious_prob > 0.5 ? "critical" : "ok"} label="Malicious probability" />
          </div>
        </div>
      )}
    </Card>
  );
}

function DeepLearning() {
  const c = useConsole();
  const { data, error } = useDLMetrics();
  if (error) return <ErrorState detail={error} />;
  if (!data) return <Card><SkeletonRows rows={8} /></Card>;
  if (!data.torch_available)
    return (
      <Card>
        <Empty icon="cpu" title="Deep learning is disabled on this server">
          PyTorch is not installed, so the autoencoder, CNN, LSTM and DQN models are not available.
        </Empty>
      </Card>
    );
  const { autoencoder: ae = {}, malware_cnn: cnn = {}, log_lstm: lstm = {}, dqn = {} } = data;
  const ready = data.status === "ready";
  const n3 = (v) => (v == null ? "—" : Number(v).toFixed(3));

  return (
    <>
      <div className="row row-wrap" style={{ marginBottom: 16, gap: 12 }}>
        {data.status === "training" ? (
          <Badge tone="medium" size="lg">
            <Spinner size={12} /> {data.stage || "Training…"}
          </Badge>
        ) : data.status === "error" ? (
          <Badge tone="critical" size="lg">
            Training failed{data.error ? `: ${data.error}` : ""}
          </Badge>
        ) : (
          <Badge tone="ok" size="lg" dot>
            All models trained
          </Badge>
        )}
        <span className="small muted">{data.data_note || "Trained on synthetic data."} They show how well each model learned its task, not accuracy on real traffic.</span>
      </div>
      <div className="grid cols-2">
        <ModelCard
          icon="layers"
          title="Deep autoencoder"
          purpose="Zero-day anomaly detection"
          trained={ae.trained}
          metrics={[
            ["AUC", n3(ae.auc), "Separation of normal and anomalous traffic"],
            ["Detection rate", pct(ae.detection_rate), "Anomalies caught at the chosen threshold"],
            ["False alarms", pct(ae.false_alarm_rate), "Normal traffic wrongly flagged"],
            ["Architecture", "14 → 32 → 16 → 4 → …"],
          ]}
        />
        <ModelCard
          icon="bug"
          title="Malware CNN"
          purpose="1-D convolution over raw bytes"
          trained={cnn.trained}
          metrics={[
            ["AUC", n3(cnn.auc)],
            ["Accuracy", pct(cnn.accuracy)],
            ["Input window", "256 bytes"],
            ["Validation set", cnn.val_samples != null ? num(cnn.val_samples) : "—"],
          ]}
        />
        <ModelCard
          icon="fileText"
          title="Log LSTM"
          purpose="Multi-step attack sequences in logs"
          trained={lstm.trained}
          metrics={[
            ["AUC", n3(lstm.auc)],
            ["Accuracy", pct(lstm.accuracy)],
            ["Window", "16 events"],
            ["Validation set", lstm.val_samples != null ? num(lstm.val_samples) : "—"],
          ]}
        />
        <ModelCard
          icon="target"
          title="DQN response agent"
          purpose="Recommends the SOC response"
          trained={dqn.trained}
          metrics={[
            ["Policy agreement", pct(dqn.policy_agreement), "How often it picks the best response on unseen incidents"],
            ["Policy score", pct(dqn.policy_score), "Reward versus the best possible (random policy = 0%)"],
            ["Training steps", num(dqn.train_steps || 0)],
            ["Analyst feedback", `${num(dqn.online_updates || 0)} updates`],
          ]}
        />
      </div>
      <div className="grid cols-12" style={{ marginTop: 16 }}>
        <div className="span-7">
          <CnnTry ready={ready && cnn.trained} />
        </div>
        <Card className="span-5" title="Response agent" subtitle="Ask the DQN what to do about an incident" icon="cpu">
          <p className="small dim" style={{ marginBottom: 14 }}>
            Describe an incident with eight signals (severity, anomaly, threat-intel match and more) and see the response the agent recommends, with the expected value of every
            option.
          </p>
          <Button variant="primary" iconRight="arrowRight" onClick={() => c.navigate("response", "agent")}>
            Open the response agent
          </Button>
        </Card>
      </div>
    </>
  );
}

export default function Models() {
  const c = useConsole();
  const tab = activeTab("models", c.tab);
  return (
    <>
      <PageHeader
        eyebrow="Intelligence"
        icon="cpu"
        title="Models"
        description="Monitor detection models, training metrics, and performance."
      />
      <PageTabs page="models" />
      {tab === "dl" ? <DeepLearning /> : <DetectionModels />}
    </>
  );
}
